import { useSyncExternalStore } from 'react';

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getQueryClient } from 'app/get-query-client';
import type { MarketCardPriceType } from 'constants/markets';
import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import type { ThemePreference } from 'lib/stores/colorPaletteOverrideStore';
import { HistoryPortfolioColumn, PortfolioColumn } from 'models/portfolio';
import type { GetPropertiesResponse, SetPropertiesResponse } from 'models/reqres';
import { useSnackbarStore } from 'stores/zustand/snackbarStore';
import config from 'utils/config';

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

export const userPropertiesKeys = {
    all: ['userProperties'] as const,
    user: (userId: string) => [...userPropertiesKeys.all, userId] as const,
};

export type SportsLayoutVariant = 'share-prices' | 'payout-odds' | 'american-odds';

export type SourceRecord = {
    source: string;
    timestamp: string;
};

/**
 * Typed view of the `/users/:id/properties` record, with client defaults applied.
 * The query cache itself stores the raw server record (`Record<string, unknown>`) so
 * consumers of app-specific keys (subaccount aliases, block-trade contacts, social
 * blocklists, `SportsLayoutVariant`, ...) can keep reading it directly; this shape
 * covers the shared cross-surface preferences.
 */
export type UserProperties = {
    completedOnboarding: boolean;
    completedLinkingOnboarding: boolean;
    marketCardPriceType: MarketCardPriceType;
    portfolioColumns: PortfolioColumn[];
    historicalPortfolioColumns: HistoryPortfolioColumn[];
    portfolioClosedTabMarketIds: string[];
    sources: SourceRecord[];
    usedWatchlist: boolean;
    /** Server-persisted theme preference; resolved into CSS variables by `colorPaletteBridge`. */
    colorPalette: ThemePreference | undefined;
    mayHavePlacedRestingOrder: boolean;
    completedSignupSurvey: boolean;
    alreadySignUpWithOauth: boolean;
    showEditorialContent: boolean;
    lastMobileSession: string | undefined;
    showSellButtons: boolean;
    skippedSignUp: boolean;
    flipSellAllowedWeb: boolean | undefined;
    /** Post-trade auto-sell ("take profit") prompt opt-out; shared with the mobile app's pref. */
    autoSellPromptEnabled: boolean;
    /** One-time perps leverage education card dismissal; shared with the mobile app's key. */
    seenPerpsLeverageEducation: boolean;
    /** One-time live-chat link to the perps FAQ. */
    seenPerpsLiveChatFaqBanner: boolean;
    createdCredentials: boolean;
    /** Stored under the legacy `betaSettings` server key. */
    parlaysEnabled: boolean;
    lastSearchedTerms: string[];
    liveEventBannerSeen: string[];
    attributionAnswer: string | undefined;
    seenMessagesIds: string[];
    shareReferralName: boolean;
    /**
     * ISO timestamp of the API keys page's one-time location check — set when the
     * user starts a verification there, or when a passive result is shown to them.
     */
    apiLocationCheckedAt: string | undefined;
};

/**
 * Some boolean user properties round-trip through the properties API as
 * 'true'/'false' strings; map those back to booleans and pass everything else
 * through untouched.
 */
function coerceBooleanProperty(value: unknown): unknown {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
}

const DEFAULT_PORTFOLIO_COLUMNS = [
    PortfolioColumn.LAST_TRADED,
    PortfolioColumn.CONTRACTS,
    PortfolioColumn.AVG,
    PortfolioColumn.EXPOSURE,
    PortfolioColumn.PAYOUT_IF_WIN,
    PortfolioColumn.MARKET_VALUE,
    PortfolioColumn.TOTAL_RETURN,
];

const DEFAULT_HISTORICAL_PORTFOLIO_COLUMNS = [
    HistoryPortfolioColumn.FINAL_POSITION,
    HistoryPortfolioColumn.SETTLEMENT_PAYOUT,
    HistoryPortfolioColumn.COST,
    HistoryPortfolioColumn.TOTAL_PAYOUT,
    HistoryPortfolioColumn.TOTAL_RETURN,
];

export function normalizeUserProperties(raw: Record<string, unknown> | undefined): UserProperties {
    const vo = (raw ?? {}) as Record<string, any>;
    return {
        completedOnboarding: vo.completedOnboarding ?? false,
        completedLinkingOnboarding: vo.completedLinkingOnboarding ?? true,
        marketCardPriceType: vo.marketCardPriceType ?? 'traded',
        portfolioColumns: Array.isArray(vo.portfolioColumns)
            ? vo.portfolioColumns.filter((value: PortfolioColumn) => Object.values(PortfolioColumn).includes(value))
            : DEFAULT_PORTFOLIO_COLUMNS,
        historicalPortfolioColumns: vo.historicalPortfolioColumns ?? DEFAULT_HISTORICAL_PORTFOLIO_COLUMNS,
        portfolioClosedTabMarketIds: vo.portfolioClosedTabMarketIds ?? [],
        sources: vo.sources ?? [],
        usedWatchlist: vo.usedWatchlist ?? false,
        colorPalette: vo.colorPalette,
        mayHavePlacedRestingOrder: vo.mayHavePlacedRestingOrder ?? true,
        completedSignupSurvey: vo.completedSignupSurvey ?? false,
        alreadySignUpWithOauth: vo.alreadySignUpWithOauth ?? false,
        showEditorialContent: vo.showEditorialContent ?? true,
        lastMobileSession: vo.lastMobileSession,
        showSellButtons: vo.showSellButtons ?? true,
        skippedSignUp: vo.skippedSignUp ?? false,
        flipSellAllowedWeb: coerceBooleanProperty(vo.flipSellAllowedWeb) as boolean | undefined,
        autoSellPromptEnabled: (coerceBooleanProperty(vo.autoSellPromptEnabled) as boolean | undefined) ?? true,
        seenPerpsLeverageEducation:
            (coerceBooleanProperty(vo.seenPerpsLeverageEducation) as boolean | undefined) ?? false,
        seenPerpsLiveChatFaqBanner:
            (coerceBooleanProperty(vo.seenPerpsLiveChatFaqBanner) as boolean | undefined) ?? false,
        createdCredentials: vo.createdCredentials ?? false,
        parlaysEnabled: vo.betaSettings ?? false,
        lastSearchedTerms: vo.lastSearchedTerms ?? [],
        liveEventBannerSeen: vo.liveEventBannerSeen ?? [],
        attributionAnswer: vo.attributionAnswer,
        seenMessagesIds: vo.seenMessagesIds ?? [],
        shareReferralName: vo.shareReferralName ?? true,
        apiLocationCheckedAt: vo.apiLocationCheckedAt,
    };
}

const defaultUserProperties = normalizeUserProperties(undefined);

export function userPropertiesQueryOptions(userId: string) {
    return queryOptions({
        queryKey: userPropertiesKeys.user(userId),
        queryFn: async ({ signal }) => {
            const response = await api.get<GetPropertiesResponse>(`/users/${userId}/properties`, {
                signal,
            });
            return response.data.user_properties ?? {};
        },
        staleTime: Infinity,
    });
}

export function useUserPropertiesQuery() {
    const userId = useAuthStore((s) => s.userId);

    return useQuery({
        ...userPropertiesQueryOptions(userId ?? ''),
        enabled: !!userId,
    });
}

/**
 * The typed/normalized read path. `properties` always has the client defaults
 * until the server record lands; gate first-load-only effects on `isLoaded`.
 */
export function useUserProperties(): { properties: UserProperties; isLoaded: boolean } {
    const userId = useAuthStore((s) => s.userId);

    const query = useQuery({
        ...userPropertiesQueryOptions(userId ?? ''),
        enabled: !!userId,
        select: normalizeUserProperties,
    });

    return {
        properties: query.data ?? defaultUserProperties,
        isLoaded: query.isSuccess,
    };
}

function subscribeToUserPropertiesCache(callback: () => void): () => void {
    return getQueryClient().getQueryCache().subscribe(callback);
}

/**
 * Read the login-bootstrapped properties cache without requiring a nearby
 * QueryClientProvider. Live-chat windows also render in isolated previews and
 * stories, while the production app has already loaded this cache at login.
 */
export function useUserPropertiesCacheSnapshot(): { properties: UserProperties; isLoaded: boolean } {
    const userId = useAuthStore((state) => state.userId);
    const rawProperties = useSyncExternalStore(
        subscribeToUserPropertiesCache,
        () =>
            userId
                ? getQueryClient().getQueryData<Record<string, unknown>>(userPropertiesKeys.user(userId))
                : undefined,
        () => undefined,
    );

    return {
        properties: normalizeUserProperties(rawProperties),
        isLoaded: !!userId && rawProperties !== undefined,
    };
}

export function useSetUserPropertiesMutation() {
    const userId = useAuthStore((s) => s.userId);
    const queryClient = useQueryClient();

    const queryKey = userPropertiesKeys.user(userId ?? '');

    return useMutation({
        meta: { globalErrorToast: false },
        mutationFn: async (patch: Record<string, unknown>) => {
            if (!useAuthStore.getState().hasBroadWriteAccess) {
                throw new Error('Sub-users without broad write access cannot update properties');
            }
            if (!userId) {
                throw new Error('Cannot update user properties without a userId');
            }
            const response = await api.patch<SetPropertiesResponse>(`/users/${userId}/properties`, {
                user_properties: patch,
            });
            return response.data.user_properties ?? {};
        },
        onMutate: async (patch) => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<Record<string, unknown>>(queryKey);
            // Snapshot only the fields this patch touches, not the whole record — on
            // rollback we must not clobber a concurrent mutation's later writes to
            // other fields.
            const previousPatchValues =
                previous === undefined
                    ? undefined
                    : Object.fromEntries(Object.keys(patch).map((key) => [key, previous[key]]));
            queryClient.setQueryData<Record<string, unknown>>(queryKey, (current) => ({
                ...current,
                ...patch,
            }));
            return { previousPatchValues };
        },
        onError: (err, _vars, context) => {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Failed to save your preferences. Please try again.',
                variant: 'negative',
            });
            if (context?.previousPatchValues !== undefined) {
                queryClient.setQueryData<Record<string, unknown>>(queryKey, (current) => ({
                    ...current,
                    ...context.previousPatchValues,
                }));
            } else {
                // Cache was empty or evicted before the mutation — the optimistic write has
                // no known-good baseline to restore, so force a refetch to re-sync with the
                // server. Without this, staleTime: Infinity would leave the stuck value
                // forever until a hard refresh.
                void queryClient.invalidateQueries({ queryKey });
            }
        },
        onSuccess: (data) => {
            queryClient.setQueryData(queryKey, data);
        },
    });
}

/**
 * Imperative read for code that can't use hooks (zustand stores, query functions,
 * transport callbacks). Returns the defaults when the cache is empty — same
 * pre-load semantics as `useUserProperties`.
 */
export function getUserPropertiesSnapshot(): UserProperties {
    const userId = useAuthStore.getState().userId;
    if (!userId) return defaultUserProperties;
    return normalizeUserProperties(
        getQueryClient().getQueryData<Record<string, unknown>>(userPropertiesKeys.user(userId)),
    );
}

/**
 * Populate the cache during login bootstrap. The color-palette bridge reacts to the
 * cache write, so awaiting this before clearing the login `loading` flag keeps the
 * user's palette applied before first authenticated paint. Errors are swallowed —
 * login must not fail because a preferences fetch did.
 *
 * Uses `fetchQuery` (not `ensureQueryData`) with `staleTime: 0` so bootstrap always
 * awaits a real GET, rather than trusting a partial cache entry seeded by an earlier
 * `updateUserProperties(patch, userIdOverride)` call (e.g. `createdCredentials` /
 * `alreadySignUpWithOauth`, written before login finishes and `setUserId` runs).
 * `ensureQueryData` returns cached data immediately whenever it's present, even if
 * stale — it only fires an unawaited background revalidation, and only when
 * `revalidateIfStale` is set — so it can't be used to force-confirm with the server.
 */
export async function ensureUserPropertiesLoaded(): Promise<void> {
    const userId = useAuthStore.getState().userId;
    if (!userId) return;
    try {
        await getQueryClient().fetchQuery({ ...userPropertiesQueryOptions(userId), staleTime: 0 });
    } catch {
        // Non-fatal: consumers fall back to defaults and RQ retries on next mount.
    }
}

/** Record that the user has used the watchlist; no-op once set. */
export function markWatchlistUsed(): void {
    if (getUserPropertiesSnapshot().usedWatchlist) return;
    updateUserProperties({ usedWatchlist: true });
}

/**
 * Record that the API keys page's location check has run for this user, so its
 * notice does not come back on a later visit. No-op once set — the first
 * outcome is the one worth keeping.
 */
export function markApiLocationChecked(): void {
    if (getUserPropertiesSnapshot().apiLocationCheckedAt) return;
    updateUserProperties({ apiLocationCheckedAt: new Date().toISOString() });
}

/** Append an id to `seenMessagesIds` once; no-op if it was already recorded. */
export function markMessageSeen(seenMessagesId: string): void {
    const { seenMessagesIds } = getUserPropertiesSnapshot();
    if (seenMessagesIds.includes(seenMessagesId)) return;
    updateUserProperties({ seenMessagesIds: [...seenMessagesIds, seenMessagesId] });
}

/**
 * Imperative write-through for code that can't use `useSetUserPropertiesMutation`.
 * Optimistically merges into the cache, PATCHes the server, and reconciles with the
 * server echo. Silently no-ops for sub-users without broad write access (parity with
 * the legacy transport-layer guard).
 *
 * `userIdOverride` covers login/signup callbacks that run before the authStore has
 * the new user id.
 */
export function updateUserProperties(patch: Record<string, unknown>, userIdOverride?: string): void {
    const userId = userIdOverride || useAuthStore.getState().userId;
    if (!userId || !useAuthStore.getState().hasBroadWriteAccess) return;

    const queryClient = getQueryClient();
    const queryKey = userPropertiesKeys.user(userId);
    const previous = queryClient.getQueryData<Record<string, unknown>>(queryKey);
    // Snapshot only the fields this patch touches, not the whole record — on
    // rollback we must not clobber a concurrent call's later writes to other fields.
    const previousPatchValues =
        previous === undefined ? undefined : Object.fromEntries(Object.keys(patch).map((key) => [key, previous[key]]));
    queryClient.setQueryData<Record<string, unknown>>(queryKey, (current) => ({
        ...current,
        ...patch,
    }));

    api.patch<SetPropertiesResponse>(`/users/${userId}/properties`, { user_properties: patch })
        .then((response) => {
            queryClient.setQueryData(queryKey, response.data.user_properties ?? {});
        })
        .catch(() => {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Failed to save your preferences. Please try again.',
                variant: 'negative',
            });
            if (previousPatchValues !== undefined) {
                queryClient.setQueryData<Record<string, unknown>>(queryKey, (current) => ({
                    ...current,
                    ...previousPatchValues,
                }));
            } else {
                void queryClient.invalidateQueries({ queryKey });
            }
        });
}
